import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

export default function ExchangeRates() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const isSuperAdmin = user?.role === 'superadmin';

  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingCurrency, setEditingCurrency] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [updating, setUpdating] = useState(false);
  const [editingSpread, setEditingSpread] = useState(false);
  const [spreadEditValue, setSpreadEditValue] = useState('');
  const [updatingSpread, setUpdatingSpread] = useState(false);

  useEffect(() => {
    fetchRates();
    // Don't auto-refetch - let parent components control refresh
    // to avoid race conditions and flickering updates
  }, []);

  const fetchRates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/exchange-rates`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to fetch exchange rates');

      const data = await response.json();
      setRates(data);
    } catch (err) {
      console.error('Exchange rates error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="border rounded-lg p-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
        <p className="text-body-sm" style={{ color: '#b8a68f' }}>Loading exchange rates...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border rounded-lg p-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
        <p className="text-body-sm" style={{ color: '#c9a961' }}>Unable to load exchange rates</p>
      </div>
    );
  }

  if (!rates) {
    return null;
  }

  const handleSaveRate = async (currency) => {
    const newRate = parseFloat(editValue);
    if (isNaN(newRate) || newRate <= 0) {
      alert('Please enter a valid positive number');
      return;
    }

    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/exchange-rates/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          currency: currency,
          rate: newRate,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        alert(data.message || 'Failed to update exchange rate');
        return;
      }

      setRates({
        ...rates,
        [currency]: { rate: newRate },
      });
      setEditingCurrency(null);
      setEditValue('');
    } catch (err) {
      console.error('Update error:', err);
      alert('Failed to update exchange rate');
    } finally {
      setUpdating(false);
    }
  };

  // Single shared spread, DB-backed (app_wellis.topup_settings
  // .member_buy_spread_percent, superadmin-editable) -- both the admin
  // reference display and the member-facing Buying/Selling rates use
  // this same number, so there's one source of truth instead of two
  // constants that could silently drift apart.
  const spreadPercent = rates.member_buy_spread_percent ?? 1.5;
  const MEMBER_BUY_SPREAD = 1 + spreadPercent / 100;
  const MEMBER_SELL_SPREAD = 1 - spreadPercent / 100;
  const ADMIN_SELL_SPREAD = 1 - spreadPercent / 100;
  const ADMIN_BUY_SPREAD = 1 + spreadPercent / 100;
  const usdtFeePercent = rates.usdt_fee_percent;

  const handleSaveSpread = async () => {
    const newSpread = parseFloat(spreadEditValue);
    if (isNaN(newSpread) || newSpread < 0 || newSpread > 100) {
      alert('Please enter a valid percentage between 0 and 100');
      return;
    }

    setUpdatingSpread(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/credit-topup-settings-update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ member_buy_spread_percent: newSpread }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        alert(data.message || 'Failed to update spread');
        return;
      }

      setRates({ ...rates, member_buy_spread_percent: newSpread });
      setEditingSpread(false);
      setSpreadEditValue('');
    } catch (err) {
      console.error('Update spread error:', err);
      alert('Failed to update spread');
    } finally {
      setUpdatingSpread(false);
    }
  };

  const renderCurrencyRates = (currencyCode) => {
    if (!rates[currencyCode]) return null;

    const baseRate = parseFloat(rates[currencyCode].rate);
    const sellSpread = isAdmin ? ADMIN_SELL_SPREAD : MEMBER_SELL_SPREAD;
    const buySpread = isAdmin ? ADMIN_BUY_SPREAD : MEMBER_BUY_SPREAD;
    const sellRate = baseRate * sellSpread;
    const buyRate = baseRate * buySpread;

    return (
      <div key={currencyCode} className="space-y-3 p-4 border rounded" style={{ backgroundColor: '#152214', borderColor: '#3d5a4a' }}>
        <div className="flex items-center justify-between">
          <div className="font-medium" style={{ color: '#c9a961' }}>
            {currencyCode} / Credits
          </div>
          {isAdmin && editingCurrency !== currencyCode && (
            <button
              onClick={() => {
                setEditingCurrency(currencyCode);
                setEditValue(baseRate.toString());
              }}
              className="px-3 py-1 text-xs rounded" style={{ backgroundColor: '#c9a961', color: '#000' }}
            >
              Edit
            </button>
          )}
        </div>

        {editingCurrency === currencyCode ? (
          <div className="space-y-2">
            <div>
              <label className="text-xs" style={{ color: '#b8a68f' }}>Mid-Market Rate</label>
              <input
                type="number"
                step="0.0001"
                min="0"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                disabled={updating}
                className="w-full px-2 py-1 text-sm rounded border" style={{ borderColor: '#3d5a4a' }}
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleSaveRate(currencyCode)}
                disabled={updating}
                className="flex-1 px-3 py-1 text-sm rounded text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
              >
                {updating ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => setEditingCurrency(null)}
                disabled={updating}
                className="flex-1 px-3 py-1 text-sm rounded text-gray-700 bg-gray-300 hover:bg-gray-400 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className={`grid ${isAdmin ? 'grid-cols-3' : 'grid-cols-1'} gap-4 text-body-sm`}>
            {isAdmin && (
              <div>
                <p style={{ color: '#b8a68f' }} className="text-xs mb-1">Mid-Market</p>
                <p className="font-medium" style={{ color: '#c9a961' }}>
                  {baseRate.toFixed(4)}
                </p>
              </div>
            )}
            {isAdmin && (
              <div>
                <p style={{ color: '#b8a68f' }} className="text-xs mb-1">-{spreadPercent.toFixed(2)}%</p>
                <p className="font-medium text-red-400">
                  {sellRate.toFixed(4)}
                </p>
              </div>
            )}
            <div>
              <p style={{ color: '#b8a68f' }} className="text-xs mb-1">
                {isAdmin ? `+${spreadPercent.toFixed(2)}%` : 'Buying'}
              </p>
              <p className="font-medium text-green-400">
                {buyRate.toFixed(4)}
              </p>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="border rounded-lg p-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-headline-sm font-bold" style={{ color: '#c9a961' }}>
          Exchange Rates {isAdmin && <span style={{ fontSize: '0.8em', color: '#b8a68f' }}>(Admin)</span>}
        </h3>
        <button
          onClick={fetchRates}
          disabled={loading}
          className="px-3 py-1 text-xs rounded font-medium"
          style={{ backgroundColor: '#c9a961', color: '#000' }}
          title="Refresh rates from database"
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>
      {isAdmin && (
        <p className="text-body-xs mb-4" style={{ color: '#b8a68f' }}>
          You can edit exchange rates below. Spreads shown are for administrative reference (+{spreadPercent.toFixed(2)}%/-{spreadPercent.toFixed(2)}%).
        </p>
      )}
      {isSuperAdmin && (
        <div className="mb-6 p-4 border rounded" style={{ backgroundColor: '#152214', borderColor: '#3d5a4a' }}>
          <div className="flex items-center justify-between">
            <p style={{ color: '#b8a68f' }} className="text-xs">
              Buy/Sell Spread <span style={{ color: '#999999' }}>(applies to both this reference view and the member Buying rate)</span>
            </p>
            {!editingSpread && (
              <button
                onClick={() => { setEditingSpread(true); setSpreadEditValue(spreadPercent.toString()); }}
                className="px-3 py-1 text-xs rounded"
                style={{ backgroundColor: '#c9a961', color: '#000' }}
              >
                Edit
              </button>
            )}
          </div>
          {editingSpread ? (
            <div className="mt-2 flex gap-2 items-center">
              <input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={spreadEditValue}
                onChange={(e) => setSpreadEditValue(e.target.value)}
                disabled={updatingSpread}
                className="w-24 px-2 py-1 text-sm rounded border"
                style={{ borderColor: '#3d5a4a' }}
              />
              <span style={{ color: '#b8a68f' }} className="text-sm">%</span>
              <button
                onClick={handleSaveSpread}
                disabled={updatingSpread}
                className="px-3 py-1 text-sm rounded text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
              >
                {updatingSpread ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => { setEditingSpread(false); setSpreadEditValue(''); }}
                disabled={updatingSpread}
                className="px-3 py-1 text-sm rounded text-gray-700 bg-gray-300 hover:bg-gray-400 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          ) : (
            <p className="font-medium mt-1" style={{ color: '#c9a961' }}>{spreadPercent.toFixed(2)}%</p>
          )}
        </div>
      )}
      <div className="space-y-3">
        {renderCurrencyRates('USD')}
        {renderCurrencyRates('SGD')}
        {renderCurrencyRates('RMB')}
        {renderCurrencyRates('TWD')}
      </div>
      {typeof usdtFeePercent === 'number' && (
        <div className="mt-3 p-4 border rounded" style={{ backgroundColor: '#152214', borderColor: '#3d5a4a' }}>
          <p style={{ color: '#b8a68f' }} className="text-xs mb-1">USDT Top-Up Fee</p>
          <p className="font-medium" style={{ color: '#c9a961' }}>{usdtFeePercent.toFixed(2)}%</p>
        </div>
      )}
    </div>
  );
}
